/** «ЗП» — ровно то, что отдаёт `GET /api/salary/weeks`. */
import { apiFetch, query } from "@/lib/api";
import type { Place } from "@/lib/bonus";

export type SalaryWeek = {
  year: number;
  week: number;
  from: string;
  to: string;
  /** Неделя ещё идёт: часы и гарантию CRM начислит в понедельник. */
  open: boolean;
  /** Минут в акцепте. */
  minutes: number;
  shifts: number;
  days: { date: string; minutes: number; total: number }[];
  /** Начислено за неделю, как считает legacy `zpCalc`. */
  total: number;
  /** Вычеты, которые не поместились в премии (≤ 0): legacy их не удерживает. */
  minus_left: number;
  hours: number;
  salary: number;
  bonus: number;
  per_hour: number | null;
  /** Часы × гарантированная ставка; null — гарантии нет или часов меньше минимума. */
  guarantee_floor: number | null;
  /** Заявки на выплату ЗП за эту неделю; null — заявки нет. */
  paid: number | null;
};

export type SalaryModule = {
  id: number;
  name: string;
  rule: string | null;
  manual: boolean;
  /** По неделям, в том же порядке, что `weeks`. */
  totals: number[];
  counts: number[];
};

export type SalarySection = {
  id: "hours" | "salary" | "bonus";
  name: string;
  totals: number[];
  categories: { id: number; name: string; totals: number[]; modules: SalaryModule[] }[];
};

export type Salary = {
  user: Place & { rate: number };
  guarantee: { min_hours: number; price: number } | null;
  weeks: SalaryWeek[];
  sections: SalarySection[];
  /** Можно ли смотреть чужую зарплату; без права `staff` и `locations` пусты. */
  can_all: boolean;
  staff: Place[];
  locations: Place[];
};

export const WEEKS = [4, 8, 12, 26];
export const DEFAULT_WEEKS = 4;

export function loadSalary(p: { weeks: number; to: string | null; user: number | null; location: number }): Promise<Salary> {
  return apiFetch<Salary>(`/api/salary/weeks${query(p)}`);
}

/** Период вкладки «Ставка в час» — `GET /api/salary/rate`, модуль `billing/salary_rate.py`. */
export type RatePeriod = {
  from: string;
  to: string;
  weeks: number[];
  minutes: number;
  shifts: number;
  total: number;
  /** Доплата до гарантии за период. */
  guarantee: number;
  /** Эффективная ставка: всё начисленное ÷ часы акцепта. */
  per_hour: number | null;
  /** То же без доплаты до гарантии — сколько заработал часами и премиями сам. */
  own_per_hour: number | null;
  bonus_per_hour: number | null;
  /** Ставка часа и гарантия последней недели периода — по проводкам. */
  base_rate: number | null;
  guarantee_rate: number | null;
  guarantee_share: number | null;
};

export type RateRow = {
  user: Place & { rate: number };
  guarantee: { min_hours: number; price: number } | null;
  periods: RatePeriod[];
  changes: { kind: "base_rate" | "guarantee_rate"; week: number; from_date: string; was: number; now: number }[];
  delta: { per_hour: number; per_hour_pct: number; own_per_hour: number | null } | null;
};

export type SalaryRate = { rows: RateRow[]; whole: boolean; can_all: boolean; staff: Place[]; locations: Place[] };

export const RATE_WEEKS = [8, 12, 26];
export const DEFAULT_RATE_WEEKS = 12;

export function loadSalaryRate(p: { users: number[]; weeks: number; to: string | null; location: number }): Promise<SalaryRate> {
  const users = p.users.map((u) => `&users=${u}`).join("");
  return apiFetch<SalaryRate>(`/api/salary/rate${query({ weeks: p.weeks, to: p.to, location: p.location })}${users}`);
}

/** Вкладка «Индексация» — `GET /api/salary/index`, модуль `billing/salary_index.py`. */
export type IndexStatus = "due" | "ok" | "above" | "fixed" | "no_shifts";

export type IndexRow = {
  user: Place;
  /** Лестница по роли: 2 «Стилист», 4 «Комплектовщик», 6 «Руководитель» (лестница стилиста). */
  role: { id: number; name: string };
  location: { id: number | null; name: string | null };
  /** Ставка часа сейчас, `sUser.zpHour`. */
  rate: number;
  /** Гарантированная ставка часа, `sUser.zpGarantPriceHour`; 0 — гарантии нет. */
  guarantee: number;
  created: string | null;
  /** Статус «Ат-1/Ат-2» в периоде — справка: его ставит Робот по онлайн-тесту, на старт не влияет. */
  attestation: string | null;
  first_shift: string | null;
  /** Первый рабочий день: первая смена с акцептом после последнего увольнения или дата HR вручную. */
  start: string | null;
  start_reason: "first" | "return" | "manual";
  /** Старт по CRM (первая смена с акцептом) — виден рядом с ручной датой HR. */
  start_auto: string | null;
  /** Заметка HR к ручной дате старта. */
  start_note: string | null;
  returned: string | null;
  /** Старт до 20.11.2023: ставка на 04.11.2026 — как есть, считаем годовщины после. */
  legacy: boolean;
  /** Подняли за несколько дней до годовщины — по стажу. */
  early: boolean;
  /** Ставка при уходе была выше стажа — лестницу от неё не строим, ставка вручную. */
  leave_manual: boolean;
  /** В списке HR «без индексации» — заметка («старший СПб»); `null` — не в списке. */
  fixed_note: string | null;
  /** Ставка старта: первый год по лестнице или ставка на день увольнения у вернувшегося. */
  base: number;
  gaps: { from: string; to: string; days: number; dismissal: boolean }[];
  history: { date: string; was: number | null; now: number | null }[];
  last_day: string | null;
  /** Без акцепта больше 90 дней. */
  idle: boolean;
  years: number | null;
  /** Ставка по правилу на сегодня. */
  expected: number | null;
  status: IndexStatus;
  /** С какой годовщины ставка ниже положенной. */
  overdue_since: string | null;
  next_date: string | null;
  next_rate: number | null;
  /** Годовщина в ближайшие 30 дней. */
  soon: boolean;
};

export type IndexLadder = { role: number; name: string; start: number; steps: number[]; step: number };

export type SalaryIndex = {
  today: string;
  rule: { ladders: IndexLadder[]; gap_days: number; legacy_before: string; baseline: string };
  summary: { total: number; due: number; soon: number; ok: number; above: number; fixed: number; returned: number; idle: number };
  rows: IndexRow[];
};

/** «Индексировать» — `GET/POST /api/salary/index/{id}/raise`, модуль `billing/salary_raise.py`. */
export type SalaryRaise = {
  id: number;
  user_id: number;
  old: number;
  new: number;
  status: "crm_done" | "sent" | "send_failed";
  error: string | null;
  text: string | null;
  date: string | null;
  sent: string | null;
};

export type SalaryRaisePreview = {
  user: Place;
  role: { id: number; name: string };
  old: number;
  new: number | null;
  since: string | null;
  years: number | null;
  next_date: string | null;
  next_rate: number | null;
  text: string;
  to: { tg_id: number; login: string | null } | null;
  telegram_ok: boolean;
  telegram_error: string | null;
  blocked: string | null;
  last: SalaryRaise | null;
};

export function loadSalaryRaise(user: number): Promise<SalaryRaisePreview> {
  return apiFetch<SalaryRaisePreview>(`/api/salary/index/${user}/raise`);
}

export function applySalaryRaise(user: number, p: { old: number; new: number; text: string; send: boolean }): Promise<{ raise: SalaryRaise }> {
  return apiFetch<{ raise: SalaryRaise }>(`/api/salary/index/${user}/raise`, { method: "POST", body: JSON.stringify(p) });
}

export function resendSalaryRaise(id: number): Promise<{ raise: SalaryRaise }> {
  return apiFetch<{ raise: SalaryRaise }>(`/api/salary/index/raise/${id}/send`, { method: "POST" });
}

/** «Исправить дату старта» (`start` — ГГГГ-ММ-ДД) / «Вернуть по CRM» (`start = null`). */
export function setSalaryIndexStart(user: number, start: string | null, note: string): Promise<{ user_id: number; start: string | null; note: string | null }> {
  return apiFetch(`/api/salary/index/${user}/start`, { method: "POST", body: JSON.stringify({ start, note }) });
}

export function setSalaryIndexException(user: number, on: boolean, note: string): Promise<{ user_id: number; fixed: boolean; note: string | null }> {
  return apiFetch(`/api/salary/index/${user}/exception`, { method: "POST", body: JSON.stringify({ on, note }) });
}

export function loadSalaryIndex(): Promise<SalaryIndex> {
  return apiFetch<SalaryIndex>("/api/salary/index");
}
